const SENSITIVE_QUERY_KEYS =
  /^(access_token|token|authorization|code|phone|phone_no|password|secret|session|api_key|apikey|email|latitude|longitude)$/i

const EXACT_SENSITIVE_DATA_KEYS = new Set([
  "access_token",
  "authorization",
  "authorization_code",
  "password",
  "secret",
  "partner_secret",
  "phone",
  "phone_no",
  "email",
  "latitude",
  "longitude",
  "token",
  "session",
  "code_verifier",
  "verifier",
  "refresh_token",
  "unique_device_id",
  "user_id",
])

const BE_ERROR_CODE_PATTERN = /^BE-[A-Z]+-\d+$/

const SENSITIVE_URL_SEGMENTS = /(access_token|authorization|Bearer)[^&\s]*/gi

function shouldRedactDataKey(key: string, value: unknown): boolean {
  if (EXACT_SENSITIVE_DATA_KEYS.has(key.toLowerCase())) return true

  if (key === "code") {
    if (typeof value === "string" && BE_ERROR_CODE_PATTERN.test(value)) {
      return false
    }
    return true
  }

  return false
}

export function hashUserId(value: string): string {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i)
  }
  return `u_${(hash >>> 0).toString(36)}`
}

export function normalizeRoutePath(pathname: string): string {
  return pathname.replace(/\d+/g, ":id").replace(/[a-f0-9-]{36}/gi, ":uuid")
}

export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url

  try {
    const parsed = new URL(url, "https://local.invalid")
    const redactedParams = new URLSearchParams()

    parsed.searchParams.forEach((value, key) => {
      redactedParams.set(
        key,
        SENSITIVE_QUERY_KEYS.test(key) ? "[Filtered]" : value
      )
    })

    const path = normalizeRoutePath(parsed.pathname)
    const query = redactedParams.toString()
    return query ? `${path}?${query}` : path
  } catch {
    return url
      .replace(SENSITIVE_URL_SEGMENTS, "[Filtered]")
      .split("?")[0]
      ?.replace(/\d+/g, ":id")
  }
}

export function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 4) return "[Truncated]"
  if (value == null) return value
  if (typeof value === "string") {
    if (value.length > 500) return `${value.slice(0, 500)}…`
    return value.replace(SENSITIVE_URL_SEGMENTS, "[Filtered]")
  }
  if (typeof value !== "object") return value
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => scrubValue(item, depth + 1))
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
      key,
      shouldRedactDataKey(key, nested)
        ? "[Filtered]"
        : scrubValue(nested, depth + 1),
    ])
  )
}

export function scrubTags(
  tags: Record<string, string> | undefined
): Record<string, string> | undefined {
  if (!tags) return tags

  return Object.fromEntries(
    Object.entries(tags).map(([key, value]) => [
      key,
      key === "route" || key === "api_endpoint"
        ? normalizeRoutePath(value)
        : value,
    ])
  )
}
